import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CardHeader,
} from '@mui/material';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CheckCircleOutline, HighlightOff } from '@mui/icons-material';
import Iconify from '../../../components/iconify';
import useAccount from '../../../_mock/account';

UserItem.propTypes = {
  user: PropTypes.shape({
    fullName: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    emailVerified: PropTypes.bool.isRequired,
    phoneNumber: PropTypes.number.isRequired,
    promoCode: PropTypes.string.isRequired,
    referralCount: PropTypes.number.isRequired,
    role: PropTypes.string.isRequired,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
};

export default function UserItem({ user }) {
  const account = useAccount();
  const token = localStorage.getItem('token');
  const [formData, setFormData] = useState({
    addressLine1: user.addressLine1 || '',
    addressLine2: user.addressLine2 || '',
    city: user.city || '',
    state: user.state || '',
    postalCode: user.postalCode || '',
    country: user.country || '',
  });

  const [editMode, setEditMode] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const apiUrl = `${process.env.REACT_APP_API_URL}/updateUserAddress`;
      const response = await axios.post(apiUrl, formData, config);

      if (response.status === 200) {
        toast.success(response.data.message, { position: toast.POSITION.TOP_RIGHT });
        setEditMode(false);
      } else {
        toast.error(response.data.message, { position: toast.POSITION.TOP_RIGHT });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  return (
    <Grid container spacing={3}>
      {/* User Profile Section */}
      <Grid item xs={12} md={3}>
        <Card
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: 2,
          }}
        >
          <Avatar
            src={account.photoURL}
            alt={user.fullName}
            sx={{
              width: 150,
              height: 150,
              border: '4px solid #fff',
              boxShadow: 3,
            }}
          />
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
            {user.fullName}
          </Typography>
        </Card>
      </Grid>

      {/* Profile Details Section */}
      <Grid item xs={12} md={9}>
        <Card elevation={3}>
          <CardHeader title="Personal Details" />
          <CardContent>
            <Table>
              <TableBody>
                {[
                  { label: 'Full Name', value: user.fullName },
                  { label: 'User Name', value: user.userName },
                  { label: 'Email', value: user.email },
                  {
                    label: 'Email Verified',
                    value: user.emailVerified ? (
                      <CheckCircleOutline sx={{ color: 'green' }} />
                    ) : (
                      <HighlightOff sx={{ color: 'red' }} />
                    ),
                  },
                  { label: 'Phone Number', value: user.phoneNumber },
                  { label: 'Promo Code', value: user.promoCode },
                  { label: 'Referral Count', value: user.referralCount },
                ].map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{row.label}</TableCell>
                    <TableCell>:</TableCell>
                    <TableCell>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      {/* Address Details Section */}
      <Grid item xs={12}>
        <Card elevation={3}>
          <CardHeader title="Address Details" />
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {[
                  { name: 'addressLine1', label: 'Address Line 1' },
                  { name: 'addressLine2', label: 'Address Line 2' },
                  { name: 'city', label: 'City' },
                  { name: 'state', label: 'State' },
                  { name: 'postalCode', label: 'Postal Code' },
                  { name: 'country', label: 'Country' },
                ].map((field) => (
                  <Grid item xs={12} md={4} key={field.name}>
                    <TextField
                      fullWidth
                      name={field.name}
                      label={field.label}
                      value={formData[field.name]}
                      onChange={handleChange}
                      autoComplete={field.name}
                      variant="outlined"
                      size="small"
                      disabled={!editMode}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Grid container justifyContent="center" spacing={2}>
                    {editMode ? (
                      <>

                        <Grid item>
                          <Button
                            variant="outlined"
                            type="button"
                            color="error"
                            onClick={() => setEditMode(false)}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        
                        <Grid item>
                          <Button
                            variant="contained"
                            type="submit"
                            color='primary'
                          >
                            Submit
                          </Button>
                        </Grid>

                      </>
                    ) : (
                      <Grid item>
                        <Button
                          variant="contained"
                          type="button"
                          color='primary'
                          onClick={() => setEditMode(true)}
                          startIcon={<Iconify icon="eva:edit-fill" />}
                        >
                          Address Details
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
